import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight, Globe, Smartphone, Gauge, Search, MousePointerClick,
  CheckCircle2, XCircle, AlertTriangle, Lock, Star,
  Zap, Shield, Clock, TrendingUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Logo } from "../components/Logo";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function getBenchmarkRange(score) {
  if (score <= 30) return "50–65%";
  if (score <= 50) return "38–50%";
  if (score <= 70) return "28–38%";
  if (score <= 85) return "15–25%";
  return "5–12%";
}

function ScoreRing({ score, size = 80, delay = 0, label }) {
  const [animated, setAnimated] = useState(0);
  const radius = (size - 10) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (animated / 100) * circumference;
  const color = animated >= 70 ? "#16a34a" : animated >= 40 ? "#d97706" : "#dc2626";

  useEffect(() => {
    const t = setTimeout(() => {
      let cur = 0;
      const iv = setInterval(() => {
        cur += 2;
        if (cur >= score) { setAnimated(score); clearInterval(iv); }
        else setAnimated(cur);
      }, 16);
      return () => clearInterval(iv);
    }, delay);
    return () => clearTimeout(t);
  }, [score, delay]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth="7" />
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="7"
            strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 0.3s ease" }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold" style={{ color }}>{animated}</span>
        </div>
      </div>
      {label && <span className="text-xs text-slate-500 font-medium text-center">{label}</span>}
    </div>
  );
}

function AnalysisLoader() {
  const [visibleSteps, setVisibleSteps] = useState(0);
  const steps = [
    { icon: Gauge, text: "Analysiere Ladezeit…" },
    { icon: Smartphone, text: "Prüfe mobile Optimierung…" },
    { icon: Search, text: "Bewerte SEO-Struktur…" },
    { icon: MousePointerClick, text: "Analysiere Conversion-Elemente…" },
    { icon: TrendingUp, text: "Erstelle deinen Report…" },
  ];

  useEffect(() => {
    const timers = steps.map((_, i) =>
      setTimeout(() => setVisibleSteps(v => Math.max(v, i + 1)), i * 1200)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="text-center py-16 px-4">
      <div className="relative w-20 h-20 mx-auto mb-8">
        <div className="w-20 h-20 rounded-full border-4 border-slate-200 border-t-indigo-600 animate-spin" />
        <Search className="w-8 h-8 text-indigo-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Webseite wird analysiert…</h2>
      <p className="text-slate-500 text-sm mb-10">Das dauert ca. 30 Sekunden</p>
      <div className="max-w-xs mx-auto space-y-3 text-left">
        {steps.map(({ icon: Icon, text }, i) => (
          <div
            key={i}
            className={`flex items-center gap-3 text-sm transition-all duration-500 ${
              i < visibleSteps ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
            }`}
          >
            <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
              i < visibleSteps ? "bg-indigo-100 text-indigo-600" : "bg-slate-100 text-slate-400"
            }`}>
              {i < visibleSteps - 1
                ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                : <Icon className="w-4 h-4" />
              }
            </div>
            <span className={i < visibleSteps ? "text-slate-700" : "text-slate-400"}>{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WebsiteCheckPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("form");
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const emailRef = useRef(null);

  const startCheck = async () => {
    if (!url.trim()) return setError("Bitte gib eine URL ein.");
    setError("");
    setStep("loading");
    try {
      const res = await axios.post(`${API}/website-check`, {
        url: url.trim(),
        email: email.trim() || "check@pending.de"
      }, { timeout: 60000 });
      setResult(res.data);
      if (email.trim() && email.includes("@")) {
        setStep("results");
      } else {
        setStep("teaser");
      }
    } catch (e) {
      setError(
        e.response?.status === 404
          ? "Diese Funktion ist noch nicht verfügbar. Bitte später erneut versuchen."
          : e.response?.data?.detail || "Analyse fehlgeschlagen. Bitte prüfe die URL und versuche es erneut."
      );
      setStep("form");
    }
  };

  const unlockResults = async () => {
    if (!email.trim() || !email.includes("@")) return setError("Bitte gib eine gültige E-Mail ein.");
    setError("");
    try {
      await axios.post(`${API}/website-check`, { url: url.trim(), email: email.trim() }, { timeout: 60000 });
    } catch {}
    setStep("results");
  };

  const handleCTAClick = () => {
    const params = new URLSearchParams();
    if (url.trim()) params.set("domain", url.trim().replace(/^https?:\/\//, "").split("/")[0]);
    if (result?.detected_industry) params.set("industry", result.detected_industry);
    navigate(`/assistent?${params.toString()}`);
  };

  const overall = result?.analysis?.scores?.overall ?? 0;
  const benchmarkRange = getBenchmarkRange(overall);
  const scoreColor = overall >= 70 ? "text-green-600" : overall >= 40 ? "text-amber-600" : "text-red-600";
  const scoreGradient = overall >= 70 ? "#16a34a" : overall >= 40 ? "#d97706" : "#dc2626";
  const businessName = result?.business_name || "";
  const topWeaknesses = result?.analysis?.improvements?.slice(0, 3) || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="px-4 md:px-8 py-5 border-b border-slate-100 bg-white/80 backdrop-blur-lg sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="check-logo">
            <Logo size="md" clickable={false} />
          </button>
          {(step === "results" || step === "teaser") && (
            <Button
              onClick={() => { setStep("form"); setResult(null); setEmail(""); setUrl(""); }}
              variant="outline"
              className="h-9 rounded-full text-sm border-slate-200 text-slate-600"
              data-testid="check-new-btn"
            >
              Neuen Check starten
            </Button>
          )}
        </div>
      </header>

      <main className="px-4 md:px-8 py-10 md:py-16">
        <div className="max-w-2xl mx-auto">

          {/* ===== FORM ===== */}
          {step === "form" && (
            <div data-testid="check-form">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-700 text-sm font-medium border border-indigo-100 mb-6">
                  <Zap className="w-3.5 h-3.5" />
                  Kostenlos &amp; in 60 Sekunden
                </div>
                <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4 leading-tight tracking-tight">
                  Wie viele Kunden verliert<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">
                    deine Webseite gerade?
                  </span>
                </h1>
                <p className="text-slate-500 text-base sm:text-lg max-w-lg mx-auto">
                  Wir analysieren SEO, Ladezeit und Conversion-Struktur – in unter 60 Sekunden.
                </p>
              </div>

              {/* Form Card */}
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 sm:p-8 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Webseiten-URL *</label>
                  <div className="relative">
                    <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      data-testid="check-url-input"
                      type="text" value={url} onChange={e => setUrl(e.target.value)}
                      placeholder="www.deine-webseite.de"
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-slate-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-base bg-white"
                      onKeyDown={e => e.key === "Enter" && startCheck()}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    E-Mail <span className="text-slate-400 font-normal">(optional – für Report per E-Mail)</span>
                  </label>
                  <input
                    data-testid="check-email-input"
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="deine@email.de"
                    className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-base bg-white"
                    onKeyDown={e => e.key === "Enter" && startCheck()}
                  />
                </div>

                {error && <p className="text-sm text-red-600 font-medium" data-testid="check-error">{error}</p>}

                <Button
                  data-testid="check-submit-btn"
                  onClick={startCheck}
                  className="w-full h-13 py-4 rounded-xl bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 hover:from-indigo-500 hover:via-blue-500 hover:to-violet-500 text-white text-base font-bold shadow-lg shadow-indigo-500/25 transition-all"
                >
                  Webseite kostenlos prüfen
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                {/* DSGVO Consent */}
                <p className="text-center text-xs text-slate-400" data-testid="check-dsgvo-consent">
                  Mit Klick auf „Kostenlos analysieren" akzeptierst du unsere{" "}
                  <Link to="/datenschutz" className="text-indigo-500 hover:underline" target="_blank" rel="noopener noreferrer">
                    Datenschutzerklärung
                  </Link>
                  .
                </p>
              </div>

              {/* Feature Pills */}
              <div className="flex flex-wrap justify-center gap-3 mt-7">
                {[
                  { icon: Smartphone, text: "Mobile-Check" },
                  { icon: Gauge, text: "Ladezeit" },
                  { icon: Search, text: "SEO-Score" },
                  { icon: MousePointerClick, text: "Conversion-Killer" },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm text-sm text-slate-600">
                    <Icon className="w-4 h-4 text-indigo-500" /> {text}
                  </div>
                ))}
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap items-center justify-center gap-6 mt-6" data-testid="check-trust-badges">
                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                  <Lock className="w-3.5 h-3.5" /> Kein Login erforderlich
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                  <Shield className="w-3.5 h-3.5" /> DSGVO-konform
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                  <Clock className="w-3.5 h-3.5" /> Ergebnis in 30 Sek.
                </div>
              </div>

              {/* Testimonial */}
              <div className="mt-8 bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-4 shadow-sm" data-testid="check-testimonial">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
                  TM
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    {[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                  </div>
                  <p className="text-sm text-slate-600 italic">
                    "Der Check hat sofort gezeigt, warum meine Seite keine Anfragen bringt. Die neue Seite über WebGenerator hat alles verändert."
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Thomas M. – Maler aus Köln</p>
                </div>
              </div>
            </div>
          )}

          {/* ===== LOADING ===== */}
          {step === "loading" && (
            <div data-testid="check-loading">
              <AnalysisLoader />
            </div>
          )}

          {/* ===== EMAIL GATE (TEASER) ===== */}
          {step === "teaser" && result?.analysis && (
            <div className="text-center" data-testid="check-teaser">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Deine Analyse ist fertig!</h2>
              <p className="text-slate-500 mb-8 text-sm">Gib deine E-Mail ein, um den vollständigen Report freizuschalten.</p>

              <div className="relative mb-8">
                <div className="blur-sm pointer-events-none select-none bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
                  <div className="text-center mb-4">
                    <div className="inline-flex items-center justify-center w-28 h-28 rounded-full border-4 border-indigo-200 bg-indigo-50">
                      <span className={`text-4xl font-bold ${scoreColor}`}>{overall}</span>
                    </div>
                    <p className="text-slate-400 text-sm mt-2">Webseiten-Score</p>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    {["Mobil","Speed","SEO","Conv."].map((l, i) => (
                      <ScoreRing key={l} score={[30,45,52,40][i]} size={60} label={l} />
                    ))}
                  </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/95 backdrop-blur rounded-2xl border border-slate-200 shadow-2xl p-6 w-full max-w-sm mx-4">
                    <div className="w-10 h-10 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center mx-auto mb-3">
                      <Search className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-1 text-center">Report freischalten</h3>
                    <p className="text-xs text-slate-500 mb-4 text-center">Dein Score ist berechnet. Gib deine E-Mail ein, um alle Details zu sehen.</p>
                    <input
                      ref={emailRef}
                      data-testid="check-teaser-email"
                      type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="deine@email.de"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm mb-3"
                      onKeyDown={e => e.key === "Enter" && unlockResults()}
                      autoFocus
                    />
                    {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
                    <Button
                      data-testid="check-unlock-btn"
                      onClick={unlockResults}
                      className="w-full h-11 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white text-sm font-bold"
                    >
                      Vollständigen Report anzeigen
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                    <p className="text-[11px] text-slate-400 mt-2 text-center">
                      Kein Spam. Jederzeit abbestellbar.{" "}
                      <Link to="/datenschutz" className="text-indigo-400 hover:underline" target="_blank">Datenschutz</Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===== RESULTS ===== */}
          {step === "results" && result?.analysis && (
            <div data-testid="check-results" className="space-y-6">
              {/* Personalized headline */}
              <div className="text-center mb-2">
                {businessName ? (
                  <p className="text-indigo-600 text-sm font-semibold tracking-wide uppercase mb-1" data-testid="check-business-name">
                    {businessName} – hier ist dein Report
                  </p>
                ) : (
                  <p className="text-indigo-600 text-sm font-semibold tracking-wide uppercase mb-1">
                    Dein Webseiten-Report ist fertig
                  </p>
                )}
                <h2 className="text-3xl font-extrabold text-slate-900">
                  Webseiten-Score: <span className={scoreColor}>{overall}/100</span>
                </h2>
              </div>

              {/* Score Card */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="check-score-card">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-slate-500 text-sm font-medium mb-1">Gesamt-Score</p>
                    <p className={`text-5xl font-black ${scoreColor}`}>{overall}</p>
                    <p className="text-slate-400 text-xs mt-0.5">von 100 Punkten</p>
                  </div>
                  <div className="relative w-24 h-24">
                    <svg viewBox="0 0 96 96" className="-rotate-90 w-full h-full">
                      <circle cx="48" cy="48" r="40" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                      <circle cx="48" cy="48" r="40" fill="none" stroke={scoreGradient} strokeWidth="8"
                        strokeDasharray={251} strokeDashoffset={251 - (overall / 100) * 251}
                        strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s ease" }} />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className={`text-2xl font-black ${scoreColor}`}>{overall}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3" data-testid="check-scores">
                  <ScoreRing score={result.analysis.scores.mobile ?? 0} label="Mobil" size={64} delay={0} />
                  <ScoreRing score={result.analysis.scores.performance ?? 0} label="Speed" size={64} delay={200} />
                  <ScoreRing score={result.analysis.scores.seo ?? 0} label="SEO" size={64} delay={400} />
                  <ScoreRing score={result.analysis.scores.conversion ?? 0} label="Conversion" size={64} delay={600} />
                </div>

                {/* Benchmark warning */}
                <div className="mt-5 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="check-benchmark">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      Basierend auf Branchen-Benchmarks verlieren Webseiten mit ähnlichem Score durchschnittlich{" "}
                      <span className="font-bold text-amber-700">{benchmarkRange} potenzielle Anfragen</span>{" "}
                      – bevor ein Besucher überhaupt Kontakt aufnimmt.
                    </p>
                  </div>
                </div>
              </div>

              {/* Top 3 Weaknesses */}
              {topWeaknesses.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="check-weaknesses">
                  <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    Die 3 kritischsten Schwachstellen
                  </h3>
                  <div className="space-y-3">
                    {topWeaknesses.map((w, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-red-50 border border-red-100">
                        <XCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{w.title}</p>
                          <p className="text-slate-500 text-xs mt-0.5">{w.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* THE BRIDGE */}
              <div className="rounded-2xl overflow-hidden border border-indigo-100 shadow-sm" data-testid="check-bridge">
                <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 px-6 py-5">
                  <p className="text-indigo-200 text-xs font-bold uppercase tracking-wider mb-1">Die Lösung</p>
                  <h3 className="text-xl font-extrabold text-white">So behebst du das in Minuten</h3>
                  <p className="text-indigo-100/80 text-sm mt-1">
                    Mit WebGenerator erstellst du automatisch eine optimierte Version deiner Webseite.
                  </p>
                </div>

                <div className="bg-white px-6 py-5">
                  <div className="space-y-3 mb-6">
                    {[
                      "KI-generierte SEO-Struktur",
                      "Conversion-optimiertes Layout",
                      "Performance-optimiertes Hosting",
                      "Sofort live auf deiner Domain",
                    ].map(text => (
                      <div key={text} className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                        </div>
                        <span className="text-slate-700 text-sm font-medium">{text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Price anchor */}
                  <div className="bg-slate-50 rounded-xl p-3 mb-5 flex items-center gap-3 border border-slate-200">
                    <Shield className="w-5 h-5 text-indigo-500 shrink-0" />
                    <p className="text-xs text-slate-600">
                      Eine Agentur verlangt dafür <span className="line-through text-slate-400">1.500€</span> –
                      bei uns automatisiert <span className="text-indigo-600 font-bold">ab 29€/Monat</span>.
                    </p>
                  </div>

                  <Button
                    data-testid="check-upsell-cta"
                    onClick={handleCTAClick}
                    className="w-full h-13 py-4 rounded-xl bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 hover:from-indigo-500 hover:via-blue-500 hover:to-violet-500 text-white font-bold text-base shadow-lg shadow-indigo-500/25 transition-all"
                  >
                    Optimierte Webseite jetzt erstellen
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <div className="flex items-center justify-center gap-4 mt-3">
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Weniger als 5 Minuten
                    </span>
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Kein Login nötig
                    </span>
                  </div>
                </div>
              </div>

              {/* Strengths (secondary) */}
              {result.analysis.strengths?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="check-strengths">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-500" /> Das macht deine Seite gut
                  </h3>
                  <ul className="space-y-1.5">
                    {result.analysis.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-500">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" />{s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

    </div>
  );
}
